import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import {
  addMonths,
  differenceInMonths,
  endOfMonth,
  startOfMonth,
  startOfToday,
} from "date-fns/esm";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";

import useMonthOptions from "@app/component/input/dateRangeSelect/utils/useMonthOptions";
import RoundButton from "@app/component/RoundButton";
import useView from "@app/component/view/utils/useView";
import { getFirstDateWithData } from "@app/scene/reports/store/mileageTracking/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { formatRawDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";
import parseRange from "@app/util/range/parseRange";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./CommonFilterStyles.module.scss";

const CLEAR_OPTION_VALUE = "clear";

export interface MonthFilterProps {
  fromDate: Date | undefined;
}

const MonthFilter: FC<MonthFilterProps> = ({ fromDate }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    updateFilterModel,
    viewState: { filter },
  } = useView();

  const condition = filter[TripFieldName.START_TIME];
  const value = useMemo(() => {
    if (!condition) {
      return undefined;
    }
    if (isSimpleCondition(condition) && condition.operator === FilterOperator.IN_RANGE) {
      const date = parseApiDate(condition.value[0]);
      const difference = differenceInMonths(date, Date.now());
      if (difference <= 0) {
        return `${DateRangeType.MONTH}${difference}` as StandardRange;
      }
    }
    return undefined;
  }, [condition]);

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Month Filter"]);
      const value = info.key as StandardRange | typeof CLEAR_OPTION_VALUE;
      if (value === CLEAR_OPTION_VALUE) {
        updateFilterModel({ [TripFieldName.START_TIME]: undefined });
      } else {
        const result = parseRange(value);
        if (result) {
          const date = addMonths(startOfToday(), result.offset);
          updateFilterModel({
            [TripFieldName.START_TIME]: {
              operator: FilterOperator.IN_RANGE,
              value: [startOfMonth(date).toISOString(), endOfMonth(date).toISOString()],
            },
          });
        }
      }
    },
    [analytics, updateFilterModel]
  );

  const months = useMonthOptions(intl, fromDate);
  const menuProps = useMemo(() => {
    const items: MenuProps["items"] = months.map((monthGroup) => ({
      children: monthGroup.options.map((range) => ({
        key: range.value,
        label: range.label,
      })),
      key: monthGroup.key,
      label: monthGroup.label,
      type: "group",
    }));
    items.unshift({
      className: styles.clearMenuItem,
      key: CLEAR_OPTION_VALUE,
      label: intl.formatMessage({
        id: "reports.mileageTracking.filters.month.options.clear",
        defaultMessage: "Clear",
        description: "Clear option in the Month filter button on the Mileage Tracking page",
      }),
    });
    return {
      activeKey: value,
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, months, value]);

  return (
    <Dropdown
      disabled={!fromDate}
      menu={menuProps}
      overlayClassName={styles.dropdownOverlay}
      trigger={["click"]}
    >
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(
          {
            id: "reports.mileageTracking.filters.month.title",
            defaultMessage: "Month{hasValue, select, true {: {date}} other {}}",
            description: "Title of the Month filter button on the Mileage Tracking page",
          },
          {
            date:
              condition &&
              isSimpleCondition(condition) &&
              condition.operator === FilterOperator.IN_RANGE
                ? formatRawDate(
                    condition.value[0],
                    intl.formatMessage({
                      id: "reports.mileageTracking.filters.month.title.dateFormat",
                      defaultMessage: "MMMM yyyy",
                      description:
                        "Date format for the Title of the Month filter button on the Mileage Tracking page",
                    })
                  )
                : undefined,
            hasValue: !!value,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  fromDate: getFirstDateWithData(state),
});

export default connect(mapStateToProps)(MonthFilter);
